import {
    BookingAttemptDataDto,
    BookingAttemptDto,
    BookingAttemptFeedingDto,
    BookingAttemptItemDto,
    BookingAttemptMedicationDto,
    BookingAttemptMetricsDto
} from 'actions/bookingAttempts/BookingAttemptsDto';
import { DaySchedule } from 'components/CreateBooking/Daycare/FeedingsAndMedications/UI/MedicalSchedule/MedicalSchedule';
import {
    BookingAttempt,
    BookingAttemptData,
    BookingAttemptItem,
    BookingAttemptMetrics,
    BookingAttemptType
} from 'model/BookingAttempt';
import { Feeding, Medication } from 'model/NewMultipleBooking';
import { OnlineBookingStep, OnlineBookingStepNames } from 'model/OnlineBooking';
import moment from 'moment-timezone';

// "8:00AM" — matches CSW DailySchedule's expected token format.
const formatDoseTime = (hour: number, minute: number): string => {
    const safeHour = Math.max(0, Math.min(23, hour));
    const safeMinute = Math.max(0, Math.min(59, minute));
    return moment().hour(safeHour).minute(safeMinute).format('h:mmA');
};

const convertFeeding = (dto: BookingAttemptFeedingDto): Feeding => ({
    momentTypes: dto.momentTypes,
    foodProvisionType: dto.foodProvisionType,
    foodType: dto.foodType,
    quantity: dto.quantity,
    brand: dto.brand,
    instructions: dto.instructions
});

// OLB sends `schedule: Array<{hour, minute, date?}>` plus `scheduleMode`.
// CSW expects `schedule: Array<string>` for daily and
// `specificSchedule: Array<DaySchedule>` for per-day.
const convertMedication = (dto: BookingAttemptMedicationDto, timeZone: string): Medication => {
    const entries = dto.schedule ?? [];

    if (dto.scheduleMode === 'specific-days') {
        const byDate = new Map<string, Array<string>>();
        for (const entry of entries) {
            if (!entry.date) continue;
            const key = moment.tz(entry.date, timeZone).format('YYYY-MM-DD');
            const doses = byDate.get(key) ?? [];
            doses.push(formatDoseTime(entry.hour, entry.minute));
            byDate.set(key, doses);
        }

        const specificSchedule: Array<DaySchedule> = Array.from(byDate.entries()).map(([key, doses]) => ({
            date: moment.tz(key, 'YYYY-MM-DD', timeZone),
            enabled: true,
            doses
        }));

        return {
            name: dto.name,
            methodType: dto.methodType,
            dosage: dto.dosage,
            instructions: dto.instructions,
            schedule: [],
            specificSchedule
        };
    }

    return {
        name: dto.name,
        methodType: dto.methodType,
        dosage: dto.dosage,
        instructions: dto.instructions,
        schedule: entries.map(entry => formatDoseTime(entry.hour, entry.minute)),
        specificSchedule: []
    };
};

const convertItem = (
    item: BookingAttemptItemDto,
    timeZone: string,
    fallback: { staffId?: number; date?: string; time?: string }
): BookingAttemptItem => ({
    petId: item.petId,
    serviceId: item.serviceId,
    addons: item.addons,
    // Backward compat: old grooming records have no serviceType on items — infer from top-level fields
    serviceType: item.serviceType ?? (fallback.date !== undefined ? 'grooming' : undefined),
    staffId: item.staffId ?? fallback.staffId,
    date: item.date ?? fallback.date,
    time: item.time ?? fallback.time,
    dropOffTime: item.dropOffTime,
    pickUpTime: item.pickUpTime,
    roomUnitId: item.roomUnitId,
    startDate: item.startDate,
    endDate: item.endDate,
    feedings: item.feedings?.map(convertFeeding),
    medications: item.medications?.map(med => convertMedication(med, timeZone))
});

export const convertToBookingAttempt = (dto: BookingAttemptDto, timeZone: string): BookingAttempt => ({
    id: dto.id,
    marketplaceId: dto.marketplace_id,
    name: dto.name,
    email: dto.email,
    phone: dto.phone,
    type: dto.type as BookingAttemptType,
    pets: dto.pets,
    customerId: dto.customer_id,
    bookingData: convertToBookingAttemptData(dto.booking_data, timeZone),
    abandonedStepName: OnlineBookingStepNames[dto.abandoned_step] ?? dto.abandoned_step,
    abandonedStep: dto.abandoned_step as OnlineBookingStep,
    abandonedDate: moment(dto.abandoned_date).tz(timeZone)
});

export const convertToBookingAttemptData = (dto: BookingAttemptDataDto, timeZone: string): BookingAttemptData => {
    const legacyFallback = { staffId: dto.staffId, date: dto.date, time: dto.time };

    return {
        date: dto.date,
        time: dto.time,
        notes: dto.notes,
        staffId: dto.staffId,
        dateTime:
            dto.date && dto.time
                ? moment(`${dto.date} ${dto.time}`, 'YYYY-MM-DD HH:mm A').tz(timeZone, true)
                : undefined,
        bookingItems: dto.bookingItems?.map(item => convertItem(item, timeZone, legacyFallback))
    };
};

export const convertToBookingAttemptMetrics = (dto: BookingAttemptMetricsDto): BookingAttemptMetrics => ({
    totalCount: dto.total_abandoned,
    convertedCount: dto.converted,
    convertedValue: dto.converted_value
});
